import { useAlert, useToast } from "@apex/component-library";
import { Link, Text } from "@chakra-ui/react";
import { AxiosError } from "axios";
import { Formik, FormikConfig } from "formik";
import { withFormikDevtools } from "formik-devtools-extension";
import { DateTime } from "luxon";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { Link as ReactRouterLink, useSearchParams } from "react-router-dom";

import queryKeys from "../../api/queryKeys";
import { useGetCallsAndAlerts } from "../../api/useGetCallsAndAlerts";
import { AppRoutes } from "../../routes";
import {
  CallsAlertsSearchBarValues,
  EventCode,
  GetCallsFromAPI,
} from "../../types";
import buildAndCleanQueryString from "../../utils/buildAndCleanQueryString";
import {
  API_DATE_FORMAT,
  DISPLAY_DATE_FORMAT,
} from "../../utils/dateFormatter";
import displayError from "../../utils/displayError";
import formatGetCallQueryString from "../../utils/formatGetCallQueryString";
import { formatUrlParamsToFormik } from "../../utils/formatUrlParamsToFormik";
import CallsAlertsSearchForm from "./SearchBarForm";
import validationSchema from "./SearchBarValidationSchema";

const MAX_RECORDS_DISPLAYED = 5000;

const SearchBarWrapper = () => {
  const [searchParams, setSearchParams] = useSearchParams("");
  const [searchFromState, setSearchState] = useState(validationSchema.cast({}));
  const isFirstRender = useRef(true);
  const { setAlert } = useAlert();
  const toast = useToast();
  const queryClient = useQueryClient();

  const getYesterday = (format = DISPLAY_DATE_FORMAT) => {
    return DateTime.now().minus({ days: 1 }).toFormat(format);
  };

  const { refetch: fetchGetCalls, isFetching } = useGetCallsAndAlerts(
    formatGetCallQueryString({
      searchParams,
      isFirstLoad: isFirstRender.current,
      initialQuery: `eventCodes=${EventCode.Md},${
        EventCode.Rt
      }&fromDueDate=${getYesterday(API_DATE_FORMAT)}&toDueDate=${getYesterday(
        API_DATE_FORMAT
      )}`,
    }),
    {
      onSuccess: data => {
        if (data && data.totalRows > MAX_RECORDS_DISPLAYED) {
          const exportLink = `${AppRoutes.Export}?${searchParams}`;
          setAlert({
            status: "warning",
            title: "Record limit warning",
            description: (
              <Text>
                Your search found {data.totalRows.toLocaleString()} results. The
                table displays a maximum of 5,000 records so all results are not
                displayed. You can refine your query to narrow the results or
                use the{" "}
                <Link
                  as={ReactRouterLink}
                  to={exportLink}
                  color="blue.400"
                  data-testid="banner-export-link"
                >
                  Export Tool
                </Link>
                .
              </Text>
            ),
            isClosable: true,
            showIcon: true,
          });
        }
      },
      onError: (error: AxiosError) => {
        const status = error.response?.status || error.request.status;
        if (status === 400 || status === 403 || status === 500) {
          queryClient.setQueryData(
            [queryKeys.getMarginCalls],
            (prev: GetCallsFromAPI | undefined) => ({
              totalRows: 0,
              records: [],
            })
          );
        }
        displayError(setAlert, toast, error);
      },
    }
  );

  useEffect(() => {
    const hasParams = !!searchParams.toString().length;
    if (hasParams) {
      const initialFormikParams = formatUrlParamsToFormik(searchParams, {
        removeDashes: [
          "fromTradeDate",
          "toTradeDate",
          "toDueDate",
          "fromDueDate",
        ],
        returnAsArray: ["callType", "correspondentCode", "eventName"],
      });

      setSearchState(validationSchema.cast(initialFormikParams));
    } else {
      const initialDefaultState = {
        ...searchFromState,
        callType: [EventCode.Md, EventCode.Rt],
        fromDueDate: getYesterday(),
        toDueDate: getYesterday(),
      };
      setSearchState(initialDefaultState);
      const queryString = buildAndCleanQueryString(initialDefaultState, {
        replaceSlashes: ["fromDueDate", "toDueDate"],
      });
      setSearchParams(queryString);
    }
    isFirstRender.current = false;
    fetchGetCalls();
    // Should only run on first render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearSearchParams = () => {
    setSearchState(validationSchema.cast({}));
    setSearchParams("");
  };

  const onSubmit = (values: CallsAlertsSearchBarValues) => {
    const queryString = buildAndCleanQueryString(values, {
      replaceSlashes: [
        "fromTradeDate",
        "toTradeDate",
        "fromDueDate",
        "toDueDate",
      ],
      exclude: ["eventCodes"],
    });
    setSearchParams(queryString);
    fetchGetCalls();
  };

  const formikConfig: FormikConfig<CallsAlertsSearchBarValues> = {
    initialValues: validationSchema.cast(
      searchFromState
    ) as CallsAlertsSearchBarValues,
    onSubmit,
    validationSchema,
    enableReinitialize: true,
  };

  return (
    <Formik {...formikConfig}>
      {formikProps => {
        withFormikDevtools(formikProps);
        return (
          <CallsAlertsSearchForm
            isFetching={isFetching}
            clearSearchParams={clearSearchParams}
          />
        );
      }}
    </Formik>
  );
};

export default SearchBarWrapper;
